import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Send, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

export default function PortalPlaceOrder() {
  const [products, setProducts] = useState([]);
  const [items, setItems] = useState([{ product_name: '', qty: '', unit: 'pcs', price: '' }]);
  const [notes, setNotes] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // iter199 — Mount-only fetch. `API` is a module-level constant and `axios`
  // is a module import, so neither needs to be in deps.
  useEffect(() => {
    axios.get(`${API}/portal/products`, { withCredentials: true })
      .then(r => setProducts(r.data || []));
    axios.get(`${API}/portal/me`, { withCredentials: true })
      .then(r => setDeliveryAddress(r.data?.client?.address || ''));
  }, []);

  const pickProduct = (idx, name) => {
    const p = products.find(x => x.name === name);
    setItems(arr => arr.map((it, i) => i === idx ? {
      ...it,
      product_name: name,
      product_id: p?.id || '',
      unit: p?.unit || it.unit,
      price: p?.price != null ? p.price : it.price,
      is_custom_rate: !!p?.is_custom_rate,
    } : it));
  };

  const addRow = () => setItems(a => [...a, { product_name: '', qty: '', unit: 'pcs', price: '' }]);
  const removeRow = (idx) => setItems(a => a.filter((_, i) => i !== idx));

  const subtotal = items.reduce((s, it) => s + (Number(it.qty || 0) * Number(it.price || 0)), 0);

  const submit = async () => {
    const valid = items.filter(i => i.product_name && Number(i.qty) > 0);
    if (!valid.length) return toast.error('Add at least one item');
    setSubmitting(true);
    try {
      await axios.post(`${API}/portal/orders`, {
        items: valid,
        notes,
        requested_delivery_date: deliveryDate || null,
        delivery_address: deliveryAddress,
      }, { withCredentials: true });
      toast.success('Order submitted · awaiting acceptance');
      setItems([{ product_name: '', qty: '', unit: 'pcs', price: '' }]);
      setNotes('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to submit'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-5 max-w-3xl">
      <div>
        <h1 className="font-heading text-2xl font-semibold text-zinc-50">Place a New Order</h1>
        <p className="text-zinc-500 text-sm mt-1">We'll review and confirm your order shortly.</p>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-4">
        {items.map((it, idx) => (
          <div key={idx} className="grid grid-cols-12 gap-2" data-testid={`order-row-${idx}`}>
            <div className="col-span-12 sm:col-span-5">
              <label className="text-zinc-500 text-[10px] mb-1 block">Product</label>
              <input list={`products-${idx}`} value={it.product_name}
                onChange={e => pickProduct(idx, e.target.value)}
                data-testid={`product-input-${idx}`}
                placeholder="Search or type…"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
              <datalist id={`products-${idx}`}>
                {products.map(p => <option key={p.id || p.name} value={p.name}>{p.unit} · {fmt(p.price)}</option>)}
              </datalist>
            </div>
            <div className="col-span-4 sm:col-span-2">
              <label className="text-zinc-500 text-[10px] mb-1 block">Qty</label>
              <input type="number" min="0" step="any" value={it.qty}
                data-testid={`qty-input-${idx}`}
                onChange={e => setItems(a => a.map((x, i) => i === idx ? { ...x, qty: e.target.value } : x))}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
            </div>
            <div className="col-span-3 sm:col-span-2">
              <label className="text-zinc-500 text-[10px] mb-1 block">Unit</label>
              <input value={it.unit}
                onChange={e => setItems(a => a.map((x, i) => i === idx ? { ...x, unit: e.target.value } : x))}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
            </div>
            <div className="col-span-4 sm:col-span-2">
              <label className="text-zinc-500 text-[10px] mb-1 block flex items-center gap-1">
                Rate
                {it.is_custom_rate && <span className="text-[9px] text-green-400 font-semibold" title="Your agreed rate · locked">🔒 Locked</span>}
              </label>
              <input type="number" min="0" value={it.price}
                readOnly={it.is_custom_rate}
                onChange={e => setItems(a => a.map((x, i) => i === idx ? { ...x, price: e.target.value } : x))}
                className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none ${it.is_custom_rate ? 'bg-green-500/5 border-green-500/30 text-green-300 font-mono' : 'bg-zinc-800 border-zinc-700 text-zinc-50 focus:border-amber-500'}`} />
            </div>
            <div className="col-span-1 flex items-end justify-end">
              {items.length > 1 && (
                <button onClick={() => removeRow(idx)}
                  className="p-2 rounded-lg bg-red-900/20 text-red-400 hover:bg-red-900/40">
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          </div>
        ))}
        <button onClick={addRow} data-testid="add-row-btn"
          className="flex items-center gap-1.5 text-xs text-amber-400 hover:text-amber-300">
          <Plus size={11} /> Add another item
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
          <label className="text-zinc-400 text-xs mb-1.5 block">Requested Delivery Date</label>
          <input type="date" value={deliveryDate} onChange={e => setDeliveryDate(e.target.value)}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
          <label className="text-zinc-400 text-xs mb-1.5 block">Delivery Address</label>
          <input value={deliveryAddress} onChange={e => setDeliveryAddress(e.target.value)}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
        <label className="text-zinc-400 text-xs mb-1.5 block">Notes (optional)</label>
        <textarea rows={3} value={notes} onChange={e => setNotes(e.target.value)}
          placeholder="Special instructions, labels, packaging preferences…"
          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
      </div>

      <div className="flex items-center justify-between bg-amber-500/5 border border-amber-500/20 rounded-xl p-4">
        <div>
          <div className="text-zinc-400 text-xs">Estimated Total</div>
          <div className="font-mono font-bold text-2xl text-amber-300 mt-0.5">{fmt(subtotal)}</div>
          <div className="text-zinc-500 text-[10px] mt-0.5">Tax and final price confirmed on acceptance</div>
        </div>
        <button onClick={submit} disabled={submitting} data-testid="submit-order-btn"
          className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black font-semibold text-sm px-5 py-2.5 rounded-xl disabled:opacity-50">
          {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          Submit Order
        </button>
      </div>
    </div>
  );
}
